import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';

export interface StatusBadgeProps {
  status: string;
  size?: 'sm' | 'md';
}

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  active: {
    bg: 'var(--ant-color-success-bg)',
    text: 'var(--ant-color-success-text)',
    dot: 'var(--ant-color-success-text)',
  },
  running: {
    bg: 'var(--ant-color-success-bg)',
    text: 'var(--ant-color-success-text)',
    dot: 'var(--ant-color-success-text)',
  },
  completed: {
    bg: 'var(--ant-color-info-bg)',
    text: 'var(--ant-color-info-text)',
    dot: 'var(--ant-color-info-text)',
  },
  done: {
    bg: 'var(--ant-color-info-bg)',
    text: 'var(--ant-color-info-text)',
    dot: 'var(--ant-color-info-text)',
  },
  paused: {
    bg: 'var(--ant-color-warning-bg)',
    text: 'var(--ant-color-warning-text)',
    dot: 'var(--ant-color-warning-text)',
  },
  blocked: {
    bg: 'var(--ant-color-warning-bg)',
    text: 'var(--ant-color-warning-text)',
    dot: 'var(--ant-color-warning-text)',
  },
  failed: {
    bg: 'var(--ant-color-error-bg)',
    text: 'var(--ant-color-error-text)',
    dot: 'var(--ant-color-error-text)',
  },
  cancelled: {
    bg: 'var(--ant-color-error-bg)',
    text: 'var(--ant-color-error-text)',
    dot: 'var(--ant-color-error-text)',
  },
  offline: {
    bg: 'var(--ant-color-bg-layout)',
    text: 'var(--ant-color-text-tertiary)',
    dot: 'var(--ant-color-border)',
  },
  backlog: {
    bg: 'var(--ant-color-bg-layout)',
    text: 'var(--ant-color-text-tertiary)',
    dot: 'var(--ant-color-border)',
  },
  todo: {
    bg: 'var(--ant-color-bg-layout)',
    text: 'var(--ant-color-text-tertiary)',
    dot: 'var(--ant-color-border)',
  },
  pending: {
    bg: 'var(--ant-color-bg-layout)',
    text: 'var(--ant-color-text-tertiary)',
    dot: 'var(--ant-color-border)',
  },
  in_progress: {
    bg: 'var(--ant-color-primary-bg)',
    text: 'var(--ant-color-primary-text)',
    dot: 'var(--ant-color-primary-text)',
  },
};

const DEFAULT_COLOR = {
  bg: 'var(--ant-color-bg-layout)',
  text: 'var(--ant-color-text-tertiary)',
  dot: 'var(--ant-color-border)',
};

export function StatusBadge({ status, size = 'sm' }: StatusBadgeProps): ReactElement {
  const { t } = useTranslation();
  const colors = STATUS_COLORS[status] ?? DEFAULT_COLOR;
  const isSmall = size === 'sm';
  const normalizedStatus = status.replace(/_/g, ' ');
  const statusKey = `crm.dashboard.status.${status}`;
  const translated = t(statusKey);
  const label = translated === statusKey ? normalizedStatus : translated;

  return (
    <span
      className="inline-flex items-center font-medium"
      style={{
        gap: 5,
        padding: isSmall ? '2px 8px' : '3px 10px',
        borderRadius: 999,
        fontSize: isSmall ? 10 : 11,
        backgroundColor: colors.bg,
        color: colors.text,
        textTransform: 'capitalize',
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: isSmall ? 5 : 6,
          height: isSmall ? 5 : 6,
          borderRadius: '50%',
          backgroundColor: colors.dot,
          flexShrink: 0,
        }}
      />
      {label}
    </span>
  );
}
